import React from "react";
import { Code, Package, Copy, Check, ExternalLink, ShoppingCart, Sparkles } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { toast } from "sonner";

const SCRIPT_URL = `${process.env.REACT_APP_BACKEND_URL || "https://emakurer.dk"}`.replace(/\/$/, "") + "/widget/pickup-point.js";

const CODE_BASIC = `<!-- 1. Add a container where the widget should appear -->
<div id="ema-pickup"></div>

<!-- 2. Add hidden inputs to persist the selection in your checkout form -->
<input type="hidden" name="ema_pickup_id" id="ema_pickup_id" />
<input type="hidden" name="ema_pickup_name" id="ema_pickup_name" />

<!-- 3. Load the widget -->
<script src="${SCRIPT_URL}"></script>
<script>
  EMAPickupPoint.mount('ema-pickup', {
    zip: '2300',        // customer's destination ZIP (for ranking)
    language: 'da',     // 'da' or 'en'
    onSelect: function (point) {
      document.getElementById('ema_pickup_id').value = point.id;
      document.getElementById('ema_pickup_name').value = point.shop_name;
    }
  });
</script>`;

const CODE_SHOPIFY = `<!-- In your Shopify checkout.liquid or theme product page -->
<div id="ema-pickup" data-section="ema-pickup"></div>
<script src="${SCRIPT_URL}"></script>
<script>
  EMAPickupPoint.mount('ema-pickup', {
    zip: '{{ customer.default_address.zip | default: "" }}',
    language: 'da',
    primaryColor: '#0F766E',     // match your shop's accent color
    onSelect: function (point) {
      // Save to cart attributes (visible to the merchant in order details)
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            'ema_pickup_id': point.id,
            'ema_pickup_name': point.shop_name,
            'ema_pickup_address': point.shop_address + ', ' + point.shop_zip + ' ' + point.shop_city
          }
        })
      });
    }
  });
</script>`;

const CODE_WOO = `<?php // In your WooCommerce checkout.php template ?>
<div id="ema-pickup"></div>
<input type="hidden" name="ema_pickup_id" id="ema_pickup_id" />
<input type="hidden" name="ema_pickup_name" id="ema_pickup_name" />
<script src="${SCRIPT_URL}"></script>
<script>
  EMAPickupPoint.mount('ema-pickup', {
    zip: '<?php echo esc_js(WC()->customer->get_shipping_postcode()); ?>',
    onSelect: function (point) {
      document.getElementById('ema_pickup_id').value = point.id;
      document.getElementById('ema_pickup_name').value = point.shop_name;
    }
  });
</script>`;

function CodeBlock({ code, lang, idx }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success("Kopieret til clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Kunne ikke kopiere");
    }
  };
  return (
    <div className="relative" data-testid={`code-${idx}`}>
      <button
        onClick={copy}
        className="absolute top-3 right-3 bg-white/10 hover:bg-white/20 text-white text-xs px-3 py-1.5 rounded flex items-center gap-1.5 transition-colors"
        data-testid={`copy-${idx}`}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? "Kopieret" : "Kopiér"}
      </button>
      <pre className="bg-[#0A1128] text-[#E2E8F0] text-xs overflow-x-auto p-5 rounded-xl font-mono leading-relaxed border border-white/10">
        <code>{code}</code>
      </pre>
      <div className="text-[10px] uppercase tracking-widest text-[#475569] mt-1">{lang}</div>
    </div>
  );
}

export default function WidgetDocsPage() {
  useSeo({
    title: "EMA Pickup Point Widget · Drop-in pakke-afhentning til webshops",
    description: "Tilføj EMA Pickup Point-valg til din webshop checkout med en simpel JavaScript-snippet. Virker med Shopify, WooCommerce, Magento og custom webshops.",
    path: "/widget",
  });

  const containerRef = React.useRef(null);

  // Live demo: mount the actual widget script in a sandboxed div
  React.useEffect(() => {
    if (!containerRef.current) return;
    // Inject the script
    const existing = document.querySelector('script[data-ema-pickup-widget]');
    const setup = () => {
      if (containerRef.current && window.EMAPickupPoint) {
        // Clear any previous mount
        containerRef.current.innerHTML = "";
        const target = document.createElement("div");
        containerRef.current.appendChild(target);
        window.EMAPickupPoint.mount(target, {
          language: "da",
          onSelect: (p) => toast.success(`Valgt: ${p.shop_name}`),
        });
      }
    };
    if (window.EMAPickupPoint) {
      setup();
    } else if (!existing) {
      const s = document.createElement("script");
      s.src = SCRIPT_URL;
      s.async = true;
      s.dataset.emaPickupWidget = "true";
      s.onload = setup;
      document.head.appendChild(s);
    } else {
      existing.addEventListener("load", setup);
    }
  }, []);

  return (
    <div className="bg-white" data-testid="widget-docs-page">
      {/* HERO */}
      <section className="bg-[#0A1128] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(220,38,38,0.18),transparent_55%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24 relative">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-red border border-brand-red/40 rounded-full px-3 py-1.5">
            <Code size={12} /> Webshop Widget · v1.0
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.02]">
            EMA Pickup Point.<br />
            <span className="text-brand-red">3 linjers kode</span> til din checkout.
          </h1>
          <p className="text-white/75 mt-6 text-lg max-w-3xl leading-relaxed">
            Tilbyd dine kunder afhentning hos lokale EMA Pickup Points på Sjælland med en
            drop-in JavaScript-widget. Virker på Shopify, WooCommerce, Magento og custom webshops —
            ingen API-integration nødvendigt.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#install" className="btn-primary text-base" data-testid="hero-install">
              Kom i gang <ExternalLink size={14} />
            </a>
            <a href="#demo" className="border-2 border-white/20 hover:border-white/40 text-white font-bold rounded-lg px-6 py-3 transition-colors">
              Se live demo
            </a>
          </div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          <Package size={20} className="text-brand-red mb-3" />
          <div className="font-display font-bold text-[#0A1128]">Zero dependencies</div>
          <p className="text-sm text-[#475569] mt-2">Vanilla JS, ~5 KB gzipped. Ingen jQuery, ingen React. Indlæses asynkront.</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          <ShoppingCart size={20} className="text-brand-red mb-3" />
          <div className="font-display font-bold text-[#0A1128]">Plug & play</div>
          <p className="text-sm text-[#475569] mt-2">3 linjer kode i din checkout. Shadow DOM beskytter mod CSS-konflikter. Customizable colors.</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-6">
          <Sparkles size={20} className="text-brand-red mb-3" />
          <div className="font-display font-bold text-[#0A1128]">Live data</div>
          <p className="text-sm text-[#475569] mt-2">Henter aktive pickup-points fra EMA's API i realtid. Auto-rank efter postnummer.</p>
        </div>
      </section>

      {/* LIVE DEMO */}
      <section id="demo" className="bg-[#F1F5F9] py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Live demo</div>
          <h2 className="font-display font-bold text-3xl text-[#0A1128] mt-3 tracking-tight">Sådan ser widget'en ud</h2>
          <p className="text-[#475569] mt-2 text-sm">Klik en pickup-point — du får en toast som webshoppen ville modtage et JS-callback.</p>
          <div className="mt-6 bg-white p-6 rounded-2xl border border-[#E2E8F0] shadow-sm" data-testid="widget-demo">
            <div ref={containerRef}></div>
          </div>
        </div>
      </section>

      {/* INSTALL */}
      <section id="install" className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Installation</div>
        <h2 className="font-display font-bold text-3xl text-[#0A1128] mt-3 tracking-tight">Kom i gang på 2 minutter</h2>

        <div className="mt-10 space-y-12">
          <div>
            <div className="font-display font-bold text-xl text-[#0A1128] mb-3">Basis HTML / custom webshop</div>
            <CodeBlock code={CODE_BASIC} lang="html · vanilla js" idx="basic" />
          </div>

          <div>
            <div className="font-display font-bold text-xl text-[#0A1128] mb-3">Shopify (theme.liquid eller cart.liquid)</div>
            <CodeBlock code={CODE_SHOPIFY} lang="liquid · cart attributes" idx="shopify" />
          </div>

          <div>
            <div className="font-display font-bold text-xl text-[#0A1128] mb-3">WooCommerce (checkout.php)</div>
            <CodeBlock code={CODE_WOO} lang="php · hidden inputs" idx="woo" />
          </div>
        </div>
      </section>

      {/* OPTIONS */}
      <section className="bg-[#F1F5F9] py-16">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <h2 className="font-display font-bold text-2xl text-[#0A1128] tracking-tight">Konfigurations-options</h2>
          <div className="mt-6 bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A1128] text-white text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3">Option</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-left p-3">Standard</th>
                  <th className="text-left p-3">Beskrivelse</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["zip", "string", "''", "Kundens postnummer — bruges til at sortere pickup-points med matching ZIP øverst"],
                  ["language", "'da' | 'en'", "'da'", "Sprog for UI-tekster"],
                  ["primaryColor", "string (hex)", "'#DC2626'", "Accent-farve til søgefelt focus + aktiv markør"],
                  ["darkColor", "string (hex)", "'#0A1128'", "Tekst- og brand-farve"],
                  ["showSearch", "boolean", "true", "Vis/skjul søgefelt"],
                  ["maxHeight", "number (px)", "420", "Max højde for scroll-listen"],
                  ["onSelect", "function(point)", "null", "Callback når kunden vælger en pickup-point"],
                  ["onClear", "function()", "null", "Callback når valg ryddes via API"],
                ].map(([opt, type, def, desc]) => (
                  <tr key={opt} className="border-t border-[#E2E8F0]">
                    <td className="p-3 font-mono text-xs text-brand-red">{opt}</td>
                    <td className="p-3 font-mono text-xs text-[#475569]">{type}</td>
                    <td className="p-3 font-mono text-xs text-[#475569]">{def}</td>
                    <td className="p-3 text-[#475569]">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="font-display font-bold text-lg text-[#0A1128] mt-10 tracking-tight">Programmatic API</h3>
          <div className="mt-3 bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {[
                  ["widget.setZip(zip)", "Re-rank listen efter nyt postnummer"],
                  ["widget.setQuery(text)", "Sæt søgetekst programmatisk"],
                  ["widget.getSelected()", "Returnerer den valgte pickup-point eller null"],
                  ["widget.clear()", "Ryd kundens valg"],
                  ["widget.reload()", "Genhent listen fra serveren"],
                  ["widget.destroy()", "Fjern widget'en fra DOM"],
                ].map(([sig, desc]) => (
                  <tr key={sig} className="border-t border-[#E2E8F0]">
                    <td className="p-3 font-mono text-xs text-brand-red w-1/2">{sig}</td>
                    <td className="p-3 text-[#475569]">{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* B2B API option */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="bg-[#0A1128] text-white p-8 rounded-2xl flex flex-col sm:flex-row items-start gap-6">
          <div className="flex-1">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Avanceret: Server-to-server</div>
            <h3 className="font-display font-bold text-2xl mt-3 tracking-tight">Send ordrer direkte via Genfind B2B API</h3>
            <p className="text-white/75 mt-3 text-sm leading-relaxed">
              Hvis du vil have fuld kontrol over booking-flowet (egen pris-beregning, eget checkout), kan du bruge
              vores autentificerede Genfind API. Endpoints: <code className="text-brand-red">GET /api/partners/genfind/pickup-points</code>{" "}
              og <code className="text-brand-red">POST /api/partners/genfind/bookings</code> med <code>pakkeshop_partner_id</code>.
              Kontakt <a href="mailto:partner@emakurer.dk" className="text-brand-red underline">partner@emakurer.dk</a>{" "}
              for at få udleveret en API-token.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
